import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SectionLabel } from "@/components/Editorial";
import { PageHead, CTABand } from "@/components/Shared";

function Accordion({ item, id }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-onyx/15" data-testid={`faq-${id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
        aria-expanded={open}
        data-testid={`faq-toggle-${id}`}
      >
        <span className="font-serif text-2xl leading-snug md:text-3xl">{item.q}</span>
        <span className="shrink-0 text-terracotta">{open ? <Minus strokeWidth={1.3} /> : <Plus strokeWidth={1.3} />}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="max-w-2xl pb-8">
              <p className="font-sans text-base leading-relaxed text-onyx/70">{item.a}</p>
              {item.link && (
                <Link to={item.link.to} className="label mt-5 inline-block border-b border-onyx pb-1 hover:opacity-60" data-testid={`faq-link-${id}`}>
                  {item.link.label}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const { t } = useLang();
  return (
    <>
      <PageHead label={t.faq.label} title={t.faq.title} />
      <section className="bg-petal" data-testid="faq-list">
        <div className="mx-auto max-w-[1600px] px-6 py-16 md:px-12 md:py-24">
          {t.faq.categories.map((cat, ci) => (
            <div key={cat.name} className="mb-16 grid gap-8 md:grid-cols-12">
              <div className="md:col-span-4">
                <SectionLabel num={`0${ci + 1}`}>{cat.name}</SectionLabel>
              </div>
              <div className="md:col-span-7 md:col-start-6">
                {cat.items.map((item, ii) => <Accordion key={ii} item={item} id={`${ci}-${ii}`} />)}
              </div>
            </div>
          ))}
        </div>
      </section>
      <CTABand />
    </>
  );
}
